/*
 * Purpose: Add-block menu — shared by canvas toolbar and right-click palette
 * Last modified: 2026-06-27 | Agent
 * Completeness: 95
 */
import { PRODUCT_TERMS } from '@/lib/productTerms';
import {
  BLOCK_PALETTE,
  canPlaceStartBlock,
  paletteByCategory,
  type BlockTemplate,
} from '@/lib/blockPalette';
import type { Workflow } from '@/stores/workflowStore';

interface BlockPaletteMenuProps {
  workflow: Workflow;
  onPick: (template: BlockTemplate) => void;
  className?: string;
}

function Section({
  title,
  hint,
  templates,
  onPick,
}: {
  title: string;
  hint?: string;
  templates: BlockTemplate[];
  onPick: (t: BlockTemplate) => void;
}) {
  if (!templates.length) return null;
  return (
    <>
      <div className="space-y-1">
        <p className="text-xs font-semibold text-muted-foreground px-2 py-1.5">{title}</p>
        {hint && <p className="text-[10px] text-muted-foreground px-2 pb-1">{hint}</p>}
        {templates.map((template) => (
          <button
            key={`${template.type}-${template.subtype}`}
            type="button"
            onClick={() => onPick(template)}
            className="w-full text-left px-2 py-1.5 text-sm rounded hover:bg-accent hover:text-accent-foreground cursor-pointer"
          >
            {template.label}
          </button>
        ))}
      </div>
      <div className="my-2 h-px bg-border" />
    </>
  );
}

export function BlockPaletteMenu({ workflow, onPick, className }: BlockPaletteMenuProps) {
  const showStart = canPlaceStartBlock(workflow);

  return (
    <div className={className}>
      {showStart && (
        <Section
          title={`START ${PRODUCT_TERMS.blockTypes.toUpperCase()}`}
          templates={paletteByCategory('start')}
          onPick={onPick}
        />
      )}
      <Section
        title={`GOAL ${PRODUCT_TERMS.blockTypes.toUpperCase()}`}
        templates={paletteByCategory('goal')}
        onPick={onPick}
      />
      <Section
        title={`AGENT ${PRODUCT_TERMS.blockTypes.toUpperCase()}`}
        templates={paletteByCategory('agent')}
        onPick={onPick}
      />
      <Section
        title={`DRONE ${PRODUCT_TERMS.blockTypes.toUpperCase()}`}
        templates={paletteByCategory('drone')}
        onPick={onPick}
      />
      <Section
        title={`BRANDED ${PRODUCT_TERMS.blockTypes.toUpperCase()}`}
        hint="Ideas — Director builds when purpose is clear"
        templates={paletteByCategory('branded')}
        onPick={onPick}
      />
      <Section
        title={`TRIGGER ${PRODUCT_TERMS.blockTypes.toUpperCase()}`}
        templates={paletteByCategory('trigger')}
        onPick={onPick}
      />
      <Section
        title={`TOOL ${PRODUCT_TERMS.blockTypes.toUpperCase()}`}
        templates={paletteByCategory('tool')}
        onPick={onPick}
      />
      <Section
        title={`ACTION ${PRODUCT_TERMS.blockTypes.toUpperCase()}`}
        templates={paletteByCategory('action')}
        onPick={onPick}
      />
    </div>
  );
}

/** First START template — quick empty-canvas action */
export function defaultStartTemplate(): BlockTemplate | undefined {
  return BLOCK_PALETTE.find((t) => t.type === 'start');
}
